import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";
import _objectWithoutProperties from "@babel/runtime/helpers/esm/objectWithoutProperties";
import _objectSpread from "@babel/runtime/helpers/esm/objectSpread2";
import _toConsumableArray from "@babel/runtime/helpers/esm/toConsumableArray";
import { fromJS, List, Map, OrderedMap } from 'immutable';
import { isFunction } from 'lodash-es';
import { DataSource, Field, FIELD_DEFAULT_VALUES, FormState } from './Form.models';
import { onBlur, onChange as onChangeHandler, onFocus } from './Form';

var sameName = function sameName(field1) {
  return function (field2) {
    return field1.name === field2.name;
  };
};

export var handleFormErrors = function handleFormErrors(key, message) {
  return function (results) {
    var error = results.error;

    if (error) {
      throw error.statusCode === 400 && error.message || message || 'There was an error saving.';
    }

    return key ? results[key] : results;
  };
};
export var resolveFieldConfig = function resolveFieldConfig(formOptions, bindings, fieldsFn) {
  var addFieldsParam = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : [];
  var alterFieldsParam = arguments.length > 4 && arguments[4] !== undefined ? arguments[4] : {};
  var fields = fieldsFn(formOptions)(bindings);
  var addFields = typeof addFieldsParam === 'function' ? addFieldsParam(formOptions)(bindings) : addFieldsParam;
  var alterFields = typeof alterFieldsParam === 'function' ? alterFieldsParam(formOptions)(bindings) : alterFieldsParam;

  if (fields && addFields && alterFields) {
    return OrderedMap([].concat(_toConsumableArray(fields), _toConsumableArray(addFields.filter(function (field) {
      return !fields.find(sameName(field));
    }).map(function (field) {
      return _objectSpread(_objectSpread({}, field), {}, {
        "transient": true
      });
    }))).filter(function (field) {
      return !!field;
    }).map(function (_ref) {
      var name = _ref.name,
          type = _ref.type,
          fieldConfig = _objectWithoutProperties(_ref, ["name", "type"]);

      return _objectSpread(_objectSpread(_objectSpread({}, fieldConfig), alterFields[name] || {}), {}, {
        name: name,
        type: type
      });
    }).map(function (field) {
      return [field.name, field];
    }));
  }
};
export var initializeValue = function initializeValue(type) {
  var value = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : FIELD_DEFAULT_VALUES.get(type, '');
  return type === 'map' ? OrderedMap(value).map(function (value) {
    return fromJS(value);
  }) : fromJS(value);
};
export var createField = function createField(formKey) {
  return function (_ref2) {
    var constraint = _ref2.constraint,
        constraintMessage = _ref2.constraintMessage,
        enabled = _ref2.enabled,
        helpText = _ref2.helpText,
        initialValue = _ref2.initialValue,
        label = _ref2.label,
        language = _ref2.language,
        name = _ref2.name,
        onChange = _ref2.onChange,
        options = _ref2.options,
        pattern = _ref2.pattern,
        patternMessage = _ref2.patternMessage,
        placeholder = _ref2.placeholder,
        renderAttributes = _ref2.renderAttributes,
        required = _ref2.required,
        requiredMessage = _ref2.requiredMessage,
        search = _ref2.search,
        serialize = _ref2.serialize,
        _transient = _ref2["transient"],
        type = _ref2.type,
        visible = _ref2.visible;
    return Field({
      // Derived options
      id: btoa("".concat(formKey, " ").concat(name)).replace(/=+$/, ''),
      initialValue: initializeValue(type, initialValue),
      renderAttributes: fromJS(renderAttributes),
      value: initializeValue(type, initialValue),
      // Options supporting conditional expressions,
      enabled: typeof enabled === 'function' ? false : enabled,
      label: typeof label === 'function' ? '' : label,
      options: typeof options === 'function' ? List() : fromJS(options),
      placeholder: typeof placeholder === 'function' ? '' : placeholder,
      required: typeof required === 'function' ? false : required,
      search: typeof search === 'function' ? Map() : fromJS(search),
      "transient": typeof _transient === 'function' ? false : _transient,
      visible: typeof visible === 'function' ? false : visible,
      functions: Map({
        enabled: typeof enabled === 'function' ? enabled : null,
        label: typeof label === 'function' ? label : null,
        options: typeof options === 'function' ? options : null,
        placeholder: typeof placeholder === 'function' ? placeholder : null,
        required: typeof required === 'function' ? required : null,
        search: typeof search === 'function' ? search : null,
        "transient": typeof _transient === 'function' ? _transient : null,
        visible: typeof visible === 'function' ? visible : null
      }),
      // Event handlers
      eventHandlers: Map({
        onBlur: onBlur({
          formKey: formKey,
          name: name
        }),
        onChange: onChangeHandler({
          formKey: formKey,
          type: type,
          name: name
        }),
        onFocus: onFocus({
          formKey: formKey,
          name: name
        })
      }),
      // Pass-through options
      constraint: constraint,
      constraintMessage: constraintMessage,
      helpText: helpText,
      language: language,
      name: name,
      onChange: onChange,
      pattern: pattern,
      patternMessage: patternMessage,
      requiredMessage: requiredMessage,
      serialize: serialize,
      type: type
    });
  };
};
export var createDataSource = function createDataSource(_ref3) {
  var _DataSource;

  var fn = _ref3.fn,
      params = _ref3.params,
      transform = _ref3.transform;
  var paramProp = typeof params === 'function' ? 'paramsFn' : 'params';
  return DataSource((_DataSource = {
    fn: fn
  }, _defineProperty(_DataSource, paramProp, params), _defineProperty(_DataSource, "transform", transform), _DataSource));
};
export var createFormState = function createFormState(_ref4) {
  var addDataSources = _ref4.addDataSources,
      addFields = _ref4.addFields,
      alterFields = _ref4.alterFields,
      dataSources = _ref4.dataSources,
      fields = _ref4.fields,
      formKey = _ref4.formKey,
      formOptions = _ref4.formOptions,
      onError = _ref4.onError,
      onLoad = _ref4.onLoad,
      onSave = _ref4.onSave,
      onSubmit = _ref4.onSubmit;
  return FormState({
    addFields: addFields,
    alterFields: alterFields,
    dataSources: Map(addDataSources).merge(Map(dataSources(formOptions))).map(createDataSource),
    fieldsFn: fields,
    formKey: formKey,
    formOptions: formOptions,
    onError: onError && onError(formOptions),
    onLoad: onLoad && onLoad(formOptions),
    onSave: onSave && onSave(formOptions),
    onSubmit: onSubmit && onSubmit(formOptions)
  });
};
export var buildPropertyFields = function buildPropertyFields(_ref5) {
  var isNew = _ref5.isNew,
      properties = _ref5.properties,
      getName = _ref5.getName,
      getOptions = _ref5.getOptions,
      getRequired = _ref5.getRequired,
      getSensitive = _ref5.getSensitive,
      getValue = _ref5.getValue;
  return {
    propertiesFields: properties.flatMap(function (property) {
      var name = getName(property);
      var options = isFunction(getOptions) && getOptions(property);
      var required = isFunction(getRequired) && getRequired(property);
      var sensitive = isFunction(getSensitive) && getSensitive(property);
      var value = getValue(property);
      return !sensitive || isNew ? [{
        name: "property_".concat(name),
        label: name,
        type: sensitive ? 'password' : options ? 'select' : 'text',
        required: required,
        "transient": true,
        options: options,
        initialValue: value
      }] : [{
        name: "property_".concat(name),
        label: name,
        type: 'password',
        required: required ? function (_ref6) {
          var values = _ref6.values;
          return values.get("changeProperty_".concat(name));
        } : false,
        "transient": true,
        initialValue: '',
        visible: function visible(_ref7) {
          var values = _ref7.values;
          return values.get("changeProperty_".concat(name));
        }
      }, {
        name: "changeProperty_".concat(name),
        label: "Change ".concat(name),
        type: 'checkbox',
        "transient": true,
        initialValue: false,
        onChange: function onChange(_ref8, _ref9) {
          var values = _ref8.values;
          var setValue = _ref9.setValue;

          if (values.get("property_".concat(name)) !== '') {
            setValue("property_".concat(name), '');
          }
        }
      }];
    }).toArray(),
    propertiesSerialize: function propertiesSerialize(_ref10) {
      var values = _ref10.values;
      return properties.filter(function (prop) {
        return isNew || !isFunction(getSensitive) || !getSensitive(prop) || values.get("changeProperty_".concat(getName(prop)));
      }).map(getName).reduce(function (reduction, propName) {
        return reduction.set(propName, values.get("property_".concat(propName)));
      }, Map()).toObject();
    }
  };
};
export var getComponentName = function getComponentName(field) {
  return field.type ? field.type.split('-').map(function (word) {
    return "".concat(word.charAt(0).toUpperCase()).concat(word.substring(1));
  }).join('') + 'Field' : null;
};
export var getFieldComponentProps = function getFieldComponentProps(field, readOnly) {
  return {
    dirty: field.dirty,
    enabled: readOnly ? false : field.enabled,
    errors: field.errors,
    focused: field.focused,
    helpText: field.helpText,
    id: field.id,
    label: field.label,
    language: field.type === 'code' ? field.language : undefined,
    name: field.name,
    onBlur: field.eventHandlers.get('onBlur'),
    onChange: field.eventHandlers.get('onChange'),
    onFocus: field.eventHandlers.get('onFocus'),
    options: ['attributes', 'code', 'form', 'form-multi', 'map', 'radio', 'select', 'select-multi', 'table', 'text', 'text-multi'].includes(field.type) ? field.options : undefined,
    placeholder: field.placeholder,
    renderAttributes: field.renderAttributes,
    required: field.required,
    search: ['form', 'form-multi'].includes(field.type) ? field.search : undefined,
    touched: field.touched,
    value: field.value,
    visible: field.visible
  };
};